import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'
import { PlanArtifact, artifactMeta, visibleArtifacts, type ArtifactCtx, type ArtifactId } from './artifacts'
import { Icon } from './Icon'
import { useOverlayHistory } from '../useOverlayHistory'

// DocumentFilmstrip — the fullscreen document viewer.
//
// Before this, opening a second document meant closing the first and going back
// to the dock. A plan's artifacts are one body of work — the estimate is the
// takeoff priced, the takeoff is the task board bought — and making them
// mutually exclusive hid that.
//
// Three states, because a document is not an image and cannot simply be shrunk
// to thumbnail size the way the gallery idiom assumes:
//
//   cover   off-centre. The document's headline figures only. Cheap: no panel
//           mounts, so eight documents cost eight small DOM subtrees.
//   live    the centred card, once scrolling SETTLES. Renders the real artifact.
//           Gated on settling because scrubbing past eight cards would otherwise
//           mount and discard eight heavy panels.
//   read    fills the frame. Enter, or click the centred card.
//
// The strip is a native scroll-snap container, so it scrolls, snaps and answers
// a trackpad before any JavaScript arrives. GSAP loads lazily and adds only the
// choreography on top — if it never loads, this is still a working viewer.

type Gsap = (typeof import('gsap'))['gsap']

// How far back a card sits at the edge of the centre's reach. Deep enough that
// arriving at the middle is an event; not so deep that the neighbours stop
// being legible as documents.
//
// The FADE floor is deliberately shallow. These cards hold real documents now,
// and every one of them arrives with its own contrast budget already spent —
// dimming the card spends it again, on text whose colour was chosen assuming
// full opacity. Depth is carried by scale, which costs no contrast.
const MIN_SCALE = 0.74
const MIN_FADE = 0.88

type Row = { label: string; value: string }

const money = (n: number) => `$${Math.round(n).toLocaleString()}`

// What a cover says. Derived from data the plan context already holds, so a
// cover costs no fetch and no panel — the whole reason the strip stays cheap.
function coverRows(id: ArtifactId, ctx: ArtifactCtx): Row[] {
  const { board, readiness } = ctx
  const qs = board.questions ?? []
  const tasks = board.tasks ?? []
  const openCritical = qs.filter((q) => q.critical && !(q.answer ?? '').trim()).length
  switch (id) {
    case 'questions':
      return [
        { label: 'Critical open', value: String(openCritical) },
        { label: 'Answered', value: String(qs.filter((q) => (q.answer ?? '').trim()).length) },
        { label: 'Total asked', value: String(qs.length) },
      ]
    case 'bid':
      return [
        { label: 'Total', value: readiness?.bid_total ? money(readiness.bid_total) : '—' },
        { label: 'Status', value: readiness?.bid_status ?? 'not generated' },
        { label: 'Tasks priced', value: String(tasks.length) },
      ]
    case 'takeoff':
      return [
        { label: 'Basis', value: readiness?.bid_status ? 'the estimate' : 'needs an estimate' },
        { label: 'Tasks', value: String(tasks.length) },
      ]
    case 'board': {
      const hours = tasks.reduce((s, t) => s + (t.estimate_hours ?? 0), 0)
      const trades = new Set(tasks.map((t) => t.required_skill || t.skill || '').filter(Boolean))
      return [
        { label: 'Tasks', value: String(tasks.length) },
        { label: 'Hours', value: hours ? String(Math.round(hours)) : '—' },
        { label: 'Trades', value: String(trades.size) },
      ]
    }
    case 'run':
      return [
        { label: 'Plan', value: board.status === 'finalized' ? 'finalized' : 'draft' },
        { label: 'Blocking', value: `${openCritical} critical` },
      ]
    case 'design-doc':
      return [
        { label: 'State', value: board.design_doc ? 'written' : 'not written' },
        {
          label: 'Length',
          value: board.design_doc ? `${board.design_doc.trim().split(/\s+/).length} words` : '—',
        },
      ]
    case 'models':
      return [{ label: 'Scope', value: 'which model runs each step' }]
    case 'accuracy':
      return [{ label: 'Scope', value: 'estimate vs actual' }]
  }
}

export function DocumentFilmstrip({
  startId,
  startAt = 0,
  ctx,
  onClose,
  inline = false,
}: {
  startId: ArtifactId
  /** How far down the preview had been read, 0–1. Opening at the top would
   *  throw away the reading someone had already done to decide to open it. */
  startAt?: number
  ctx: ArtifactCtx
  onClose: () => void
  /**
   * Render in the panel rather than over it — the Artifacts TAB is this
   * viewer now. Inline drops everything that only makes sense for an
   * overlay: the history entry, the maximized body class, dialog semantics,
   * and the dismiss chrome (there is nothing to dismiss to — the tab bar
   * above is how you leave). Everything else — the strip, the covers, the
   * expand, the rail — is identical, which is the point of reusing it.
   */
  inline?: boolean
}) {
  const items = visibleArtifacts(ctx)
  const startIdx = Math.max(0, items.findIndex((a) => a.id === startId))

  const [active, setActive] = useState(startIdx)
  // Which cards have their real content mounted. A GROWING set, not a single
  // index: gating on "the centred card, once scrolling settles" meant the
  // document you were scrolling toward stayed blank until you stopped and then
  // popped in — the card arrived before its contents did. Mounting the
  // neighbours too means a card is already rendered by the time it reaches the
  // centre, and never unmounting means scrolling back is instant and no panel
  // is asked to fetch itself twice.
  const [mounted, setMounted] = useState<number[]>(() =>
    [startIdx - 1, startIdx, startIdx + 1].filter((i) => i >= 0),
  )
  const [expanded, setExpanded] = useState(false)

  const viewportRef = useRef<HTMLDivElement | null>(null)
  const slideRefs = useRef<(HTMLElement | null)[]>([])
  const cardRefs = useRef<(HTMLElement | null)[]>([])
  const gsapRef = useRef<{ gsap: Gsap } | null>(null)
  // The matchMedia context owns the zoom tweens. Held in a ref because the
  // expand path has to tear it down SYNCHRONOUSLY — see toggleExpand.
  const mmRef = useRef<ReturnType<Gsap['matchMedia']> | null>(null)

  // Mounting is scheduled OFF the scroll frame. Doing it inline made a fast
  // scrub mount panels between frames, which is exactly where a dropped frame
  // shows. requestIdleCallback yields to the scroll; the timeout guarantees it
  // still lands promptly if the scroll never leaves the main thread idle.
  const movingRef = useRef(false)
  const reducedRef = useRef(false)
  const idleRef = useRef<number | undefined>(undefined)
  const wantRef = useRef(startIdx)
  const ensureMounted = useCallback(
    (i: number) => {
      wantRef.current = i
      if (idleRef.current !== undefined) return
      const run = () => {
        idleRef.current = undefined
        const c = wantRef.current
        setMounted((prev) => {
          const next = new Set(prev)
          for (const j of [c - 1, c, c + 1]) if (j >= 0 && j < items.length) next.add(j)
          return next.size === prev.length ? prev : [...next]
        })
      }
      const w = window as Window & {
        requestIdleCallback?: (cb: () => void, o?: { timeout: number }) => number
        cancelIdleCallback?: (h: number) => void
      }
      idleRef.current = w.requestIdleCallback
        ? w.requestIdleCallback(run, { timeout: 120 })
        : window.setTimeout(run, 60)
    },
    [items.length],
  )

  useEffect(
    () => () => {
      if (idleRef.current === undefined) return
      const w = window as Window & { cancelIdleCallback?: (h: number) => void }
      if (w.cancelIdleCallback) w.cancelIdleCallback(idleRef.current)
      else clearTimeout(idleRef.current)
    },
    [],
  )

  const reduced =
    typeof matchMedia === 'function' && matchMedia('(prefers-reduced-motion: reduce)').matches

  reducedRef.current = reduced

  // The hook's first argument already gates its whole effect, so passing
  // !inline neutralizes the history entry without a conditional hook call.
  const dismiss = useOverlayHistory(!inline, onClose)

  // Inline, the dock is this viewer's navigation: clicking a tile changes
  // startId, and the strip travels to it. (As an overlay startId is the
  // opening position and never changes, so this effect is inert there.)
  const goToRef = useRef<((i: number) => void) | null>(null)
  const startRef = useRef(startId)
  useEffect(() => {
    if (!inline || startId === startRef.current) return
    startRef.current = startId
    const i = items.findIndex((a) => a.id === startId)
    if (i >= 0) goToRef.current?.(i)
  }, [inline, startId, items])

  const goTo = useCallback(
    (i: number) => {
      const n = items.length
      if (!n) return
      const idx = Math.min(Math.max(i, 0), n - 1)
      slideRefs.current[idx]?.scrollIntoView({
        behavior: reduced ? 'auto' : 'smooth',
        inline: 'center',
        block: 'nearest',
      })
      movingRef.current = true
      setTimeout(() => { movingRef.current = false }, 420)
      setActive(idx)
      ensureMounted(idx)
    },
    [items.length, reduced, ensureMounted],
  )
  goToRef.current = goTo

  // The zoom. Driven by DISTANCE TO CENTRE rather than by a ScrollTrigger
  // timeline, because a timeline between two keyframes is a linear ramp — the
  // card grew at a constant rate and stopped dead at the middle, which is what
  // made it read mechanical. A cosine-squared falloff is steepest either side
  // of centre and flattens as it arrives, so a card eases open as it comes in
  // and eases shut as it leaves.
  //
  // quickTo keeps a live tween per card, so the scale CHASES the target rather
  // than being assigned it — that is the smoothness. The scroll sets a target;
  // GSAP does the interpolation on its own ticker.
  // scaleX/scaleY, NOT 'scale'. quickTo(el, 'scale') wrote `scale: none` into
  // the element and left transform as a bare translate — the value never
  // reached the transform at all, while the opacity setter beside it worked
  // fine. The transform COMPONENTS are unambiguous.
  const zoomRef = useRef<((v: number) => void)[][]>([])
  const fadeRef = useRef<((v: number) => void)[]>([])

  const magnify = useCallback(() => {
    const vp = viewportRef.current
    if (!vp || !zoomRef.current.length) return
    const mid = vp.scrollLeft + vp.clientWidth / 2
    // How far out a card still feels the centre. At 0.62 the reach was NARROWER
    // than the card pitch, so the immediate neighbour already sat on the floor
    // and the row read as one big card beside seven identical small ones. Wider
    // than the pitch puts the neighbour mid-curve, which is what makes the zoom
    // legible as a gradient: 1.00 · 0.86 · 0.74 rather than 1.00 · 0.74 · 0.74.
    const reach = vp.clientWidth * 1.15
    slideRefs.current.forEach((slide, i) => {
      const zoom = zoomRef.current[i]
      if (!slide || !zoom?.length) return
      const d = Math.min(1, Math.abs(slide.offsetLeft + slide.offsetWidth / 2 - mid) / reach)
      const f = Math.cos((d * Math.PI) / 2) ** 2 // 1 at centre → 0 at the edge of reach
      const sc = MIN_SCALE + (1 - MIN_SCALE) * f
      zoom[0](sc)
      zoom[1](sc)
      fadeRef.current[i]?.(MIN_FADE + (1 - MIN_FADE) * f)
    })
  }, [])

  // Which card is centred. A scroll listener rather than IntersectionObserver
  // because we need the NEAREST slide, not merely the visible ones — at narrow
  // widths two cards are in view at once and "visible" stops meaning centred.
  useEffect(() => {
    const vp = viewportRef.current
    if (!vp || expanded) return
    let frame = 0
    let settle: ReturnType<typeof setTimeout> | undefined
    const onScroll = () => {
      if (frame) return
      frame = requestAnimationFrame(() => {
        frame = 0
        const mid = vp.scrollLeft + vp.clientWidth / 2
        let best = 0
        let bestD = Infinity
        slideRefs.current.forEach((el, i) => {
          if (!el) return
          const d = Math.abs(el.offsetLeft + el.offsetWidth / 2 - mid)
          if (d < bestD) {
            bestD = d
            best = i
          }
        })
        magnify()
        setActive(best)
        ensureMounted(best)
        // Settle to the nearest card once the gesture ends. CSS scroll-snap
        // cannot do this job here: it re-snaps DURING the scroll, so a gesture
        // shorter than half a card never moves at all. Settling after the fact
        // keeps the scroll free while the finger is down and still lands
        // centred. Skipped while a programmatic scroll is already in flight.
        clearTimeout(settle)
        settle = setTimeout(() => {
          if (movingRef.current) return
          slideRefs.current[best]?.scrollIntoView({
            behavior: reducedRef.current ? 'auto' : 'smooth',
            inline: 'center',
            block: 'nearest',
          })
        }, 140)
      })
    }
    // A plain mouse wheel produced NOTHING. Chrome only turns a vertical wheel
    // into horizontal scrolling when nothing under the pointer wants the
    // vertical delta — and once the cards hold real documents, the card body
    // takes it. Measured: deltaX moved the strip 500px, deltaY moved it 0.
    //
    // So in strip mode the wheel moves the strip. That is the right mapping for
    // what this state is FOR: choosing a document. Reading it vertically is the
    // expanded state, one Enter away, and this handler is not attached there.
    const onWheel = (e: WheelEvent) => {
      if (Math.abs(e.deltaY) <= Math.abs(e.deltaX)) return // a real sideways gesture — leave it to the browser
      e.preventDefault()
      vp.scrollLeft += e.deltaY
    }
    vp.addEventListener('scroll', onScroll, { passive: true })
    vp.addEventListener('wheel', onWheel, { passive: false })
    return () => {
      vp.removeEventListener('scroll', onScroll)
      vp.removeEventListener('wheel', onWheel)
      cancelAnimationFrame(frame)
      clearTimeout(settle)
    }
  }, [expanded, ensureMounted, magnify])

  // Centre the active card without animating there from card 0 — on first open,
  // and again whenever the strip comes back from the reading state. Declared
  // on entering and leaving the reading state, so the strip comes back centred
  // on the document that was being read rather than wherever it was left.
  useLayoutEffect(() => {
    if (expanded) return
    slideRefs.current[active]?.scrollIntoView({ behavior: 'auto', inline: 'center', block: 'nearest' })
    // `active` deliberately omitted: this is for entering and leaving the
    // reading state, not for every scroll tick, which would fight the scroll.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expanded])


  useEffect(() => {
    if (expanded) return
    let cancelled = false
    void (async () => {
      const { gsap } = await import('gsap')
      if (cancelled) return
      gsapRef.current = { gsap }

      const mm = gsap.matchMedia()
      mm.add('(prefers-reduced-motion: no-preference)', () => {
        const cards = cardRefs.current
        zoomRef.current = cards.map((c) =>
          c
            ? [
                gsap.quickTo(c, 'scaleX', { duration: 0.45, ease: 'power3.out' }),
                gsap.quickTo(c, 'scaleY', { duration: 0.45, ease: 'power3.out' }),
              ]
            : [],
        )
        fadeRef.current = cards.map((c) =>
          c ? gsap.quickTo(c, 'opacity', { duration: 0.45, ease: 'power2.out' }) : () => {},
        )
        magnify()
        return () => {
          zoomRef.current = []
          fadeRef.current = []
        }
      })
      mmRef.current = mm
    })()
    return () => {
      cancelled = true
      mmRef.current?.revert()
      mmRef.current = null
    }
  }, [expanded, items.length, magnify])

  const toggleExpand = useCallback(() => {
    // NO Flip here, deliberately. Expanding used to animate the card from its
    // strip rect to full-frame with Flip.from(..., {absolute: true}), and that
    // cost two user-visible bugs: Flip absolutely positions the card for the
    // duration, while flipping `expanded` re-runs the zoom effect, which
    // immediately starts writing scaleX/scaleY to the SAME element. The two
    // fought, and the card was left stranded at reading geometry — measured as
    // a 460px overlap with its neighbour after collapsing, and a tall black band
    // above the content on re-entry.
    //
    // The animation was polish; being able to read a document is the feature.
    // The zoom that David actually asked for is untouched — it lives on the
    // scroll, not on this transition.
    mmRef.current?.revert()
    mmRef.current = null
    setExpanded((v) => !v)
  }, [])

  // Keyboard is the whole navigation model for anyone not on a trackpad: arrows
  // move the strip, Enter reads, Escape steps back out one level at a time
  // rather than dumping you out of the viewer from the reading state.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const t = e.target as HTMLElement | null
      if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.isContentEditable)) return
      // An overlay filmstrip can sit above an inline one. Only the top one may
      // read the keyboard, or arrows scrub both strips and Escape collapses
      // both. `panel-maximized` marks a fullscreen frame — which an inline one
      // also sets while EXPANDED, so it only defers when it is not the one
      // reading.
      if (inline && !expanded && document.body.classList.contains('panel-maximized')) return
      if (e.key === 'Escape') {
        // Inline, Escape collapses a reading card and then has nothing left to
        // do — a tab is not dismissible, and swallowing the key there would
        // steal Escape from the rest of the app.
        if (expanded) {
          e.preventDefault()
          toggleExpand()
        } else if (!inline) {
          e.preventDefault()
          dismiss()
        }
        return
      }
      if (expanded) return
      if (e.key === 'ArrowRight') {
        e.preventDefault()
        goTo(active + 1)
      } else if (e.key === 'ArrowLeft') {
        e.preventDefault()
        goTo(active - 1)
      } else if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault()
        toggleExpand()
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [active, expanded, goTo, dismiss, toggleExpand, inline])

  // Land where the preview was left. The panel mounts lazily, so this waits for
  // it to have something to scroll rather than firing at a height of zero.
  useEffect(() => {
    if (!startAt) return
    let tries = 0
    const t = setInterval(() => {
      const el = cardRefs.current[active]?.querySelector('.filmstrip-card-body') as HTMLElement | null
      const room = el ? el.scrollHeight - el.clientHeight : 0
      if (room > 0) {
        el!.scrollTop = startAt * room
        clearInterval(t)
      } else if (++tries > 20) clearInterval(t)
    }, 100)
    return () => clearInterval(t)
    // Only for the document this was opened on.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startAt])

  useEffect(() => {
    // Inline the frame lives INSIDE the panel, so the app keeps its chrome —
    // until a document is EXPANDED, which goes fullscreen (see
    // `.artifact-frame.inline.reading`) and therefore wants the app chrome out
    // of the way exactly like the overlay does.
    if (inline && !expanded) return
    document.body.classList.add('panel-maximized')
    return () => document.body.classList.remove('panel-maximized')
  }, [inline, expanded])

  const current = items[active]
  const meta = artifactMeta(current?.id ?? startId)
  if (!meta || !current) return null

  return (
    <div
      className={`artifact-frame filmstrip-frame${expanded ? ' reading' : ''}${inline ? ' inline' : ''}`}
      // Dialog semantics belong to the overlay. Inline this is a region of
      // the page — claiming aria-modal would tell a screen reader the rest of
      // the app had gone away while the tab bar above is still right there.
      role={inline ? 'region' : 'dialog'}
      aria-modal={inline ? undefined : true}
      aria-label={inline ? 'Documents' : meta.label}
    >
      <header className="artifact-frame-head">
        {/* Back exists to leave. Inline there is nowhere to leave TO until a
            card is being read, so the control appears only then. */}
        {!inline || expanded ? (
          <button className="artifact-back" onClick={expanded ? toggleExpand : dismiss} title="Back (Esc)">
            <span aria-hidden>←</span> Back
          </button>
        ) : null}
        <span className="filmstrip-count">
          <span className="sr-only">Document </span>
          {active + 1} / {items.length}
        </span>
        <span className="artifact-frame-glyph">
          <Icon name={meta.icon} size={20} />
        </span>
        <h2>{meta.label}</h2>
        <span className="artifact-frame-sum">{meta.summary(ctx)}</span>
        {inline ? null : (
          <button className="artifact-frame-close" onClick={dismiss} title="Close (Esc)">
            ✕
          </button>
        )}
      </header>

      {(
        <div
          className="filmstrip-viewport"
          ref={viewportRef}
          // A scrollable region has to be reachable from the keyboard on its own
          // account — axe fails it otherwise, and arrow-key navigation is only
          // discoverable once focus can land here.
          tabIndex={0}
          role="group"
          aria-label={`${items.length} documents — arrow keys to move, Enter to read`}
        >
          <div className="filmstrip-track">
            {items.map((a, i) => {
              const isActive = i === active
              return (
                <section
                  key={a.id}
                  className={`filmstrip-slide${isActive ? ' is-active-slide' : ''}`}
                  ref={(el) => {
                    slideRefs.current[i] = el
                  }}
                >
                  <article
                    className={`filmstrip-card${isActive ? ' is-active' : ''}`}
                    ref={(el) => {
                      cardRefs.current[i] = el
                    }}
                    onClick={(e) => {
                      // A click on something INSIDE the document is that
                      // control's click, not a request to expand. Without this
                      // guard every button in the centred card also expanded it.
                      if ((e.target as HTMLElement).closest('button, a, input, select, textarea, label, [role="button"], [contenteditable="true"]'))
                        return
                      if (expanded) return
                      if (isActive) toggleExpand()
                      else goTo(i)
                    }}
                  >
                    <header className="filmstrip-card-head">
                      <span className="filmstrip-card-glyph">
                        <Icon name={a.icon} size={16} />
                      </span>
                      <h3>{a.label}</h3>
                      <span className="filmstrip-card-sum">{a.summary(ctx)}</span>
                    </header>

                    {mounted.includes(i) || (expanded && isActive) ? (
                      <div
                        className="filmstrip-card-body"
                        // A scroll container has to be reachable from the
                        // keyboard on its own account, or the only way to read
                        // past the fold is a pointer. axe fails it as
                        // scrollable-region-focusable, and it only became one
                        // when the cards started holding live documents.
                        tabIndex={0}
                        role="group"
                        aria-label={`${a.label} — scroll to read`}
                      >
                        <PlanArtifact id={a.id} />
                      </div>
                    ) : (
                      <dl className="filmstrip-cover">
                        {coverRows(a.id, ctx).map((r) => (
                          <div key={r.label} className="filmstrip-cover-row">
                            <dt>{r.label}</dt>
                            <dd>{r.value}</dd>
                          </div>
                        ))}
                      </dl>
                    )}

                    {isActive && !expanded ? (
                      <footer className="filmstrip-card-foot">
                        <span className="filmstrip-open-hint">Enter to read</span>
                      </footer>
                    ) : null}
                  </article>
                </section>
              )
            })}
          </div>
        </div>
      )}

      {/* The rail is the only always-visible answer to "how much is there, and
          where am I in it" — the header count gives the number, this gives the
          shape, and it doubles as direct navigation. */}
      {!expanded ? (
        <nav className="filmstrip-rail" aria-label="Documents">
          {items.map((a, i) => (
            <button
              key={a.id}
              className={`filmstrip-pip${i === active ? ' on' : ''}`}
              aria-current={i === active ? 'true' : undefined}
              onClick={() => goTo(i)}
            >
              {a.label}
            </button>
          ))}
        </nav>
      ) : null}
    </div>
  )
}
